import { memo, useCallback, useMemo, useState } from 'react';
import { X, BarChart2, Type, TrendingUp, Code, Layers, Layout } from 'react-feather';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { WidgetInstance } from '@trinity/chat';
import { useDashboardBuilderEditing } from '@trinity/chat-components/containers/Dashboard/context';
import { WidgetGridWidgetsContext, WidgetMapContext } from '../../../containers/WidgetGrid/context.js';
import { WidgetSelectChartStep } from '../../WidgetGrid/WidgetSelectChartStep.js';
import { WidgetSelectMediaStep } from '../../WidgetGrid/WidgetSelectMediaStep.js';
import { WidgetSelectQuoteStep } from '../../WidgetGrid/WidgetSelectQuoteStep.js';
import { useBuilderPalette, type BuilderPaletteConfig, type LibraryCategory, type PaletteEntry } from './PaletteContext.js';

type Props = {
  open: boolean;
  onClose: () => void;
  /** If provided, called with the selected type instead of auto-adding a block */
  onSelect?: (type: string) => void;
  /** Called with the picked project widget when a `library` entry completes its second step */
  onSelectLibraryWidget?: (widget: WidgetInstance) => void;
  /** When true, the Section tile is hidden (used inside sections to prevent nesting) */
  hideSections?: boolean;
};

// NOTE: These entries must be kept in sync with the widget types registered in
// createDefaultRegistry() (default-registry.tsx). Adding a widget to the
// registry without adding it here will make it invisible in the palette.
const PALETTE_ENTRIES: PaletteEntry[] = [
  { type: 'chart', label: 'Charts', icon: BarChart2 },
  { type: 'text', label: 'Rich Text', icon: Type },
  { type: 'metric_card', label: 'Metric Card', icon: TrendingUp },
  { type: 'code', label: 'Code', icon: Code },
  { type: 'rep_dashboard', label: 'Territory-Level Insights', icon: Layout },
  {
    type: 'section',
    label: 'Section',
    icon: Layers,
  },
];

const LIBRARY_STEP_TITLES: Record<LibraryCategory, string> = {
  chart: 'Select a Chart Widget',
  kpi: 'Select a KPI Widget',
  quote: 'Select a Quote Widget',
  media: 'Select a Media Widget',
};

/**
 * WidgetPalette — modal widget chooser.
 *
 * Step 1 shows the tiles. Plain tiles call `onSelect(type)` on Next. Tiles with
 * `library` (supplied via BuilderPaletteProvider) advance to a second step that
 * lists the project's widget library; Add calls `onSelectLibraryWidget(widget)`.
 */
export const WidgetPalette = memo(function WidgetPalette({
  open,
  onClose,
  onSelect,
  onSelectLibraryWidget,
  hideSections = false,
}: Props): React.ReactElement {
  const { addBlockWithWidget } = useDashboardBuilderEditing();
  const palette = useBuilderPalette();
  const [selected, setSelected] = useState<PaletteEntry | null>(null);
  const [libraryStep, setLibraryStep] = useState<LibraryCategory | null>(null);
  const [selectedWidget, setSelectedWidget] = useState<WidgetInstance | null>(null);

  const baseEntries = palette?.entries ?? PALETTE_ENTRIES;
  const visibleEntries = hideSections ? baseEntries.filter((e) => e.type !== 'section') : baseEntries;

  const handleClose = useCallback(() => {
    setSelected(null);
    setLibraryStep(null);
    setSelectedWidget(null);
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (!selected) return;
    if (selected.library && palette) {
      setLibraryStep(selected.library);
      return;
    }
    if (onSelect) {
      onSelect(selected.type);
    } else {
      addBlockWithWidget(selected.type, {});
    }
    handleClose();
  }, [selected, palette, onSelect, addBlockWithWidget, handleClose]);

  const handleBack = useCallback(() => {
    setLibraryStep(null);
    setSelectedWidget(null);
  }, []);

  const handleAdd = useCallback(() => {
    if (!selectedWidget) return;
    onSelectLibraryWidget?.(selectedWidget);
    handleClose();
  }, [selectedWidget, onSelectLibraryWidget, handleClose]);

  const libraryPalette = libraryStep !== null ? palette : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={libraryPalette ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, px: 1 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 2, position: 'relative' }}>
        <Typography variant="h6" fontWeight={600}>
          {libraryPalette && libraryStep ? LIBRARY_STEP_TITLES[libraryStep] : 'Select Widget to Add'}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          sx={{ position: 'absolute', top: 4, right: '-6px', color: 'text.disabled' }}
        >
          <X size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
        {libraryPalette && libraryStep ? (
          <LibraryStepBody>
            <LibraryStep category={libraryStep} palette={libraryPalette} onSelectedChange={setSelectedWidget} />
          </LibraryStepBody>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
            {visibleEntries.map((entry) => (
              <WidgetTile
                key={entry.type}
                selected={selected?.type === entry.type}
                disabled={entry.disabled ?? false}
                fullWidth={entry.fullWidth ?? false}
                onClick={() => !entry.disabled && setSelected(entry)}
              >
                <entry.icon size={22} color="currentColor" style={{ marginBottom: 8 }} />
                <Typography variant="caption" fontWeight={500} noWrap sx={{ color: 'inherit' }}>
                  {entry.label}
                </Typography>
                {entry.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'inherit',
                      opacity: 0.7,
                      fontSize: '0.65rem',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                      mt: 0.25,
                    }}
                  >
                    {entry.description}
                  </Typography>
                )}
              </WidgetTile>
            ))}
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {libraryPalette ? (
            <>
              <CancelButton variant="outlined" fullWidth onClick={handleBack}>
                Back
              </CancelButton>
              <NextButton variant="contained" fullWidth disabled={!selectedWidget} onClick={handleAdd}>
                Add
              </NextButton>
            </>
          ) : (
            <>
              <CancelButton variant="outlined" fullWidth onClick={handleClose}>
                Cancel
              </CancelButton>
              <NextButton variant="contained" fullWidth disabled={!selected} onClick={handleNext}>
                Next
              </NextButton>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
});

/** Feeds the host's library into the contexts the legacy WidgetGrid picker steps read. */
function LibraryStep({
  category,
  palette,
  onSelectedChange,
}: {
  category: LibraryCategory;
  palette: BuilderPaletteConfig;
  onSelectedChange: (widget: WidgetInstance | null) => void;
}): React.ReactElement {
  const { libraryWidgets, widgetComponentMap, refetchLibraryWidgets } = palette;

  const widgetsValue = useMemo(
    () => ({
      widgets: libraryWidgets ?? [],
      widgetsMap: new Map((libraryWidgets ?? []).map((w) => [w.id, w])),
      widgetsLoading: libraryWidgets === undefined,
    }),
    [libraryWidgets],
  );

  return (
    <WidgetMapContext.Provider value={widgetComponentMap}>
      <WidgetGridWidgetsContext.Provider value={widgetsValue}>
        <LibraryStepContent
          category={category}
          onSelectedChange={onSelectedChange}
          onOpenRefetch={refetchLibraryWidgets}
        />
      </WidgetGridWidgetsContext.Provider>
    </WidgetMapContext.Provider>
  );
}

function LibraryStepContent({
  category,
  onSelectedChange,
  onOpenRefetch,
}: {
  category: LibraryCategory;
  onSelectedChange: (widget: WidgetInstance | null) => void;
  onOpenRefetch?: () => void;
}): React.ReactElement {
  if (category === 'quote') {
    return (
      <WidgetSelectQuoteStep widgetType="quote" pickerOpen onSelectedChange={onSelectedChange} onOpenRefetch={onOpenRefetch} />
    );
  }
  if (category === 'media') {
    return (
      <WidgetSelectMediaStep widgetType="media" pickerOpen onSelectedChange={onSelectedChange} onOpenRefetch={onOpenRefetch} />
    );
  }
  return (
    <WidgetSelectChartStep widgetType={category} pickerOpen onSelectedChange={onSelectedChange} onOpenRefetch={onOpenRefetch} />
  );
}

const LibraryStepBody = styled(Box)(({ theme }) => ({
  maxHeight: '60vh',
  overflowY: 'auto',
  marginBottom: theme.spacing(2),
}));

const WidgetTile = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'disabled' && prop !== 'fullWidth',
})<{ selected: boolean; disabled: boolean; fullWidth: boolean }>(
  ({ theme, selected, disabled, fullWidth }) => ({
    '--tile-primary': theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3, 2),
    gridColumn: fullWidth ? '1 / -1' : undefined,
    border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.grey[200]}`,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    cursor: disabled ? 'default' : 'pointer',
    color: (() => {
      if (disabled) return theme.palette.text.disabled;
      if (selected) return theme.palette.primary.main;
      return theme.palette.text.secondary;
    })(),
    backgroundColor: (() => {
      if (selected) return `${theme.palette.primary.main}14`;
      if (disabled) return theme.palette.action.disabledBackground;
      return theme.palette.background.paper;
    })(),
    transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
    userSelect: 'none',
    ...(!disabled &&
      !selected && {
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: `${theme.palette.primary.main}08`,
          color: theme.palette.primary.main,
        },
      }),
  })
);

const CancelButton = styled(Button)(({ theme }) => ({
  borderWidth: '2px',
  borderColor: theme.palette.grey[700],
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: 'transparent',
  '&:hover': {
    borderWidth: '2px',
    borderColor: theme.palette.grey[900],
    backgroundColor: theme.palette.grey[200],
  },
}));

const NextButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
}));
